import React, { useState, useEffect, useCallback } from "react";
import { Search, X, Camera, Upload, AlertCircle, Trash2 } from "lucide-react";
import { db } from "../services/firebase";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import "./MyRegisteredProducts.css";
import { useNavigate } from "react-router-dom";
import { useSeller } from "../contexts/SellerContext";
import Header from "../components/Header";

const CLOUDINARY_CLOUD_NAME = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME;
const CLOUDINARY_UPLOAD_PRESET = "jmiseller";
const CLOUDINARY_API_URL = `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/image/upload`;

const MyRegisteredProducts = () => {
  const navigate = useNavigate();
  const { seller } = useSeller();
  const sellerId = seller?.sellerId;
  const [activeTab, setActiveTab] = useState("All");
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("newest");
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imageUploadError, setImageUploadError] = useState("");

  const tabs = ["All", "Pending QC", "Approved", "Rejected"];

  const fetchRegisteredProducts = useCallback(async () => {
    if (!sellerId) return;

    setLoading(true);
    try {
      const docRef = doc(db, "ProductRegistrations", sellerId);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const data = docSnap.data();
        const allRegistrations = data.registrations || [];
        const sortedRegistrations = allRegistrations.sort((a, b) => {
          const dateA = new Date(a.requestTimestamp);
          const dateB = new Date(b.requestTimestamp);
          return dateB - dateA;
        });
        console.log(sortedRegistrations);
        setRegistrations(sortedRegistrations);
      } else {
        setRegistrations([]);
      }
    } catch (error) {
      console.error("Error fetching registered products:", error);
      setRegistrations([]);
    } finally {
      setLoading(false);
    }
  }, [sellerId]);

  useEffect(() => {
    fetchRegisteredProducts();
  }, [sellerId, fetchRegisteredProducts]);

  const getStatusClass = (status) => {
    switch (status) {
      case "pending_approval":
        return "pending";
      case "approved":
        return "approved";
      case "rejected":
        return "rejected";
      default:
        return "";
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case "pending_approval":
        return "Pending QC";
      case "approved":
        return "Approved";
      case "rejected":
        return "Rejected";
      default:
        return status;
    }
  };

  const getFilteredRegistrations = () => {
    let filtered = registrations;

    if (activeTab !== "All") {
      const statusMap = {
        "Pending QC": "pending_approval",
        Approved: "approved",
        Rejected: "rejected",
      };
      filtered = filtered.filter((reg) => reg.status === statusMap[activeTab]);
    }

    if (searchTerm) {
      filtered = filtered.filter((reg) => {
        const productNames = Object.keys(reg.products || {})
          .join(" ")
          .toLowerCase();
        const searchLower = searchTerm.toLowerCase();
        return (
          productNames.includes(searchLower) ||
          Object.values(reg.products || {}).some(
            (product) =>
              product.segment?.toLowerCase().includes(searchLower) ||
              product.category?.toLowerCase().includes(searchLower) ||
              product.subcategory?.toLowerCase().includes(searchLower),
          )
        );
      });
    }

    if (sortBy === "newest") {
      filtered = filtered.sort(
        (a, b) => new Date(b.requestTimestamp) - new Date(a.requestTimestamp),
      );
    } else if (sortBy === "oldest") {
      filtered = filtered.sort(
        (a, b) => new Date(a.requestTimestamp) - new Date(b.requestTimestamp),
      );
    } else if (sortBy === "name") {
      filtered = filtered.sort((a, b) => {
        const nameA = Object.keys(a.products || {})[0] || "";
        const nameB = Object.keys(b.products || {})[0] || "";
        return nameA.localeCompare(nameB);
      });
    }

    return filtered;
  };

  const getMainProductName = (products) => {
    const productNames = Object.keys(products || {});
    if (productNames.length === 0) return "No Products";
    return productNames[0];
  };

  const getCategoryPath = (product) => {
    // Now getting category path from individual product object
    return `${product.segment || ""} ${product.category || ""} ${
      product.subcategory || ""
    }`.trim();
  };

  const getFirstProductImage = (products) => {
    const productNames = Object.keys(products || {});
    if (productNames.length === 0) return null;
    const firstProduct = products[productNames[0]];
    return firstProduct.image || null;
  };

  const getSpecificationDetails = (product) => {
    const details = [];
    if (product.wastage && product.wastage !== "0" && product.wastage !== "") {
      details.push(`Wastage: ${product.wastage}%`);
    }
    if (product.setMC && product.setMC !== "0" && product.setMC !== "") {
      details.push(`Set MC: ${product.setMC}`);
    }
    if (
      product.netGramMC &&
      product.netGramMC !== "0" &&
      product.netGramMC !== ""
    ) {
      details.push(`Net Gram MC: ${product.netGramMC}`);
    }
    if (
      product.specificationMC &&
      product.specificationMC !== "0" &&
      product.specificationMC !== ""
    ) {
      details.push(`${product.specification} MC: ${product.specificationMC}`);
    }
    if (
      product.specificationGramRate &&
      product.specificationGramRate !== "0" &&
      product.specificationGramRate !== ""
    ) {
      details.push(
        `${product.specification}Gram Rate: ${product.specificationGramRate}`,
      );
    }
    return details.join(" | ") || "No specifications";
  };

  const handleEditClick = (registration) => {
    const productName = getMainProductName(registration.products);
    const productData = registration.products ? registration.products[productName] : null;

    setSelectedProduct(registration);
    setEditFormData({
      ...(productData || {}),
      productName,
      image: productData?.image || "",
      rejectionReason:
        registration.rejectionReason ||
        productData?.rejectionReason ||
        registration.reason ||
        "",
    });
    setImageUploadError("");
    setIsEditModalOpen(true);
  };

  const validateSquareImage = (file) => {
    return new Promise((resolve) => {
      if (!file.type.startsWith("image/")) {
        resolve({ valid: false, file, error: `"${file.name}" is not an image file.` });
        return;
      }

      const img = new Image();
      const objectUrl = URL.createObjectURL(file);

      img.onload = () => {
        URL.revokeObjectURL(objectUrl);
        const width = img.naturalWidth;
        const height = img.naturalHeight;
        const ratio = width / height;
        const isOneToOne = Math.abs(ratio - 1) <= 0.01;

        if (isOneToOne) {
          resolve({ valid: true, file, width, height });
        } else {
          resolve({
            valid: false,
            file,
            width,
            height,
            error: `"${file.name}" has dimensions ${width}x${height} (${ratio.toFixed(2)}:1). Only 1:1 (square) images are allowed.`,
          });
        }
      };

      img.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        resolve({ valid: false, file, error: `Failed to load image "${file.name}".` });
      };

      img.src = objectUrl;
    });
  };

  const handleImageFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setImageUploadError("");

    const validation = await validateSquareImage(file);
    if (!validation.valid) {
      setImageUploadError(validation.error);
      alert(`Only 1:1 (square) images are allowed!\n\n${validation.error}`);
      e.target.value = "";
      return;
    }

    setUploadingImage(true);

    const formDataCloud = new FormData();
    formDataCloud.append("file", file);
    formDataCloud.append("upload_preset", CLOUDINARY_UPLOAD_PRESET);

    try {
      const response = await fetch(CLOUDINARY_API_URL, {
        method: "POST",
        body: formDataCloud,
      });

      const data = await response.json();
      if (data.secure_url) {
        setEditFormData((prev) => ({
          ...prev,
          image: data.secure_url,
        }));
      } else {
        setImageUploadError("Failed to upload image. Please try again.");
      }
    } catch (error) {
      console.error("Error uploading image:", error);
      setImageUploadError("Error uploading image: " + error.message);
    } finally {
      setUploadingImage(false);
      e.target.value = "";
    }
  };

  const handleEditSubmit = async () => {
    if (!selectedProduct || !sellerId) return;
    if (!editFormData.image) {
      alert("Please upload a product photo before submitting.");
      return;
    }

    try {
      // Update local state - only update image and set status to pending_approval
      const updatedRegistrations = registrations.map((reg) => {
        if (reg.registrationId === selectedProduct.registrationId) {
          const productName = editFormData.productName;
          return {
            ...reg,
            products: {
              ...reg.products,
              [productName]: {
                ...reg.products[productName],
                image: editFormData.image,
              },
            },
            status: "pending_approval",
            approved: false,
          };
        }
        return reg;
      });

      setRegistrations(updatedRegistrations);
      setIsEditModalOpen(false);

      // Update Firestore
      const docRef = doc(db, "ProductRegistrations", sellerId);
      await updateDoc(docRef, {
        registrations: updatedRegistrations,
      });
      alert("Photo updated and submitted for review!");
    } catch (error) {
      console.error("Error updating product:", error);
      alert("Error updating product. Please try again.");
    }
  };

  const handleDeleteRegistration = async (registrationId) => {
    if (!window.confirm("Are you sure you want to delete this rejected product registration?")) return;
    try {
      const updatedRegistrations = registrations.filter((r) => r.registrationId !== registrationId);
      setRegistrations(updatedRegistrations);
      setIsEditModalOpen(false);
      const docRef = doc(db, "ProductRegistrations", sellerId);
      await updateDoc(docRef, {
        registrations: updatedRegistrations,
      });
      alert("Rejected registration deleted successfully.");
    } catch (error) {
      console.error("Error deleting registration:", error);
      alert("Failed to delete registration: " + error.message);
    }
  };

  const filteredRegistrations = getFilteredRegistrations();

  if (loading) {
    return (
      <>
        <Header title="My Registered Products" />
        <div className="loading-state">Loading...</div>
      </>
    );
  }

  return (
    <>
      <Header title="My Registered Products" />
      <div className="catalogue-container">
        <div className="tabs-container">
          {tabs.map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`registertab-button ${
                activeTab === tab ? "active" : ""
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        <div className="controls-container">
          <div className="search-container">
            <Search className="search-icon" size={20} />
            <input
              type="text"
              placeholder="Search products..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="registersearch-input"
            />
          </div>
          <div className="sort-container">
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="sort-select"
            >
              <option value="newest">Newest First</option>
              <option value="oldest">Oldest First</option>
              <option value="name">Name A-Z</option>
            </select>
          </div>
        </div>
        <div className="products-list">
          {filteredRegistrations.length === 0 ? (
            <div className="empty-state">
              <p>No products found</p>
            </div>
          ) : (
            filteredRegistrations.map((registration) => {
              const mainProductName = getMainProductName(registration.products);
              const productData = registration.products[mainProductName];
              const categoryPath = getCategoryPath(productData); // Fixed: pass productData instead of registration.products
              const statusLabel = getStatusLabel(registration.status);
              const statusClass = getStatusClass(registration.status);
              const imageUrl = getFirstProductImage(registration.products);
              const specificationDetails = getSpecificationDetails(productData);

              return (
                <div
                  key={registration.registrationId}
                  className="product-card"
                  onClick={() =>
                    registration.status === "rejected" &&
                    handleEditClick(registration)
                  }
                  style={{
                    cursor:
                      registration.status === "rejected"
                        ? "pointer"
                        : "default",
                  }}
                >
                  <div
                    className="registered-product-image"
                    onClick={() => console.log(registration.registrationId)}
                  >
                    {imageUrl ? (
                      <img
                        src={imageUrl}
                        alt={mainProductName}
                        className="product-image-placeholder"
                      />
                    ) : (
                      <div className="registered-product-image-placeholder"></div>
                    )}
                  </div>
                  <div className="product-info">
                    <h3 className="product-title">{mainProductName}</h3>
                    <p className="product-category">
                      {categoryPath} / {productData?.selectedStyleType || "N/A"}{" "}
                      / {productData?.specification || "N/A"}{" "}
                    </p>
                    <p style={{ color: "gray" }}>
                      Purity :{productData?.purity}
                    </p>
                    <p className="registered-product-specs">
                      {specificationDetails}
                    </p>
                    <span className={`status-badge ${statusClass}`}>
                      {statusLabel}
                      {registration.status === "rejected" && (
                        <span
                          style={{
                            marginLeft: "8px",
                            textDecoration: "underline",
                          }}
                        >
                          (Click to Edit)
                        </span>
                      )}
                    </span>
                    {registration.status === "rejected" &&
                      (registration.rejectionReason || registration.reason) && (
                        <p className="card-rejection-reason">
                          Reason: {registration.rejectionReason || registration.reason}
                        </p>
                      )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
      <div className="bottom-bar">
        <button
          onClick={() => navigate("/productregistration")}
          className="register-new-fab"
        >
          Register New Product
        </button>
      </div>

      {/* Edit Modal */}
      {isEditModalOpen && selectedProduct && (
        <div className="modal-overlay">
          <div className="modal-content edit-rejected-modal">
            <div className="modal-header">
              <h3>Edit Rejected Product</h3>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <button
                  type="button"
                  onClick={() => handleDeleteRegistration(selectedProduct.registrationId)}
                  title="Delete Registration"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                    background: "#fee2e2",
                    color: "#dc2626",
                    border: "1px solid #fca5a5",
                    padding: "6px 12px",
                    borderRadius: "6px",
                    cursor: "pointer",
                    fontSize: "12px",
                    fontWeight: "600",
                  }}
                >
                  <Trash2 size={14} />
                  Delete
                </button>
                <button
                  className="modal-close"
                  onClick={() => setIsEditModalOpen(false)}
                >
                  <X size={20} />
                </button>
              </div>
            </div>

            <div className="modal-body">
              {/* Rejection Reason Notice */}
              <div className="rejection-reason-alert">
                <div className="rejection-reason-header">
                  <AlertCircle size={18} className="rejection-icon" />
                  <span>Rejection Reason</span>
                </div>
                <p className="rejection-reason-text">
                  {selectedProduct?.rejectionReason ||
                    editFormData?.rejectionReason ||
                    selectedProduct?.reason ||
                    "No reason provided."}
                </p>
              </div>

              {/* Product Info Summary (Read Only) */}
              <div className="product-summary-card">
                <div className="summary-title">{editFormData.productName}</div>
                <div className="summary-details">
                  <span>{editFormData.segment || selectedProduct.segment}</span>
                  <span>/</span>
                  <span>{editFormData.category || selectedProduct.category}</span>
                  <span>/</span>
                  <span>{editFormData.productSource || selectedProduct.productSource}</span>
                  <span>/</span>
                  <span>{editFormData.selectedStyleType || "Regular"}</span>
                  <span>/</span>
                  <span>{editFormData.specification || "PLANE"}</span>
                </div>
                <div className="summary-specs">
                  {editFormData.purity && <span>Purity: <strong>{editFormData.purity}</strong></span>}
                  {editFormData.wastage && <span>Wastage: <strong>{editFormData.wastage}%</strong></span>}
                  {editFormData.setMC && <span>Set MC: <strong>{editFormData.setMC}</strong></span>}
                  {editFormData.netGramMC && <span>Net Gram MC: <strong>{editFormData.netGramMC}</strong></span>}
                  {editFormData.specificationMC && <span>{editFormData.specification} MC: <strong>{editFormData.specificationMC}</strong></span>}
                  {editFormData.specificationGramRate && <span>{editFormData.specification} Gram Rate: <strong>{editFormData.specificationGramRate}</strong></span>}
                </div>
              </div>

              {/* Only Allow Photo Upload */}
              <div className="edit-photo-section">
                <label className="edit-photo-label">
                  Upload Product Photo (1:1 Square) <span className="required-star">*</span>
                </label>
                <p className="edit-photo-subtext">
                  Upload a new 1:1 (square) photo to resolve the rejection and submit for approval.
                </p>

                <div className="photo-preview-container">
                  {editFormData.image ? (
                    <div className="current-photo-wrapper">
                      <img
                        src={editFormData.image}
                        alt="Product Preview"
                        className="edit-image-preview"
                      />
                    </div>
                  ) : (
                    <div className="empty-photo-placeholder">
                      <Camera size={36} color="#94a3b8" />
                      <span>No photo uploaded</span>
                    </div>
                  )}
                </div>

                <div className="photo-actions">
                  <input
                    type="file"
                    id="edit-photo-file-input"
                    accept="image/*"
                    onChange={handleImageFileChange}
                    style={{ display: "none" }}
                    disabled={uploadingImage}
                  />
                  <label
                    htmlFor="edit-photo-file-input"
                    className={`upload-photo-btn ${uploadingImage ? "loading" : ""}`}
                  >
                    <Upload size={16} />
                    {uploadingImage
                      ? "Uploading Photo..."
                      : editFormData.image
                      ? "Change Photo"
                      : "Upload Photo"}
                  </label>
                </div>

                {imageUploadError && (
                  <p className="photo-error-message">{imageUploadError}</p>
                )}
              </div>
            </div>

            <div className="modal-footer" style={{ display: "flex", justifyContent: "space-between" }}>
              <button
                type="button"
                onClick={() => handleDeleteRegistration(selectedProduct.registrationId)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  background: "transparent",
                  color: "#dc2626",
                  border: "1.5px solid #fca5a5",
                  padding: "8px 14px",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontWeight: "600",
                  fontSize: "13px",
                }}
              >
                <Trash2 size={15} />
                Delete
              </button>
              <div style={{ display: "flex", gap: "8px" }}>
                <button
                  className="cancel-button"
                  onClick={() => setIsEditModalOpen(false)}
                  disabled={uploadingImage}
                >
                  Cancel
                </button>
                <button
                  className="submit-button"
                  onClick={handleEditSubmit}
                  disabled={uploadingImage || !editFormData.image}
                >
                  {uploadingImage ? "Uploading..." : "Submit for Review"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MyRegisteredProducts;
